import {XIcon, CheckIcon, ChevronDownIcon} from '@primer/octicons-react'
import {Box, Button, Grid, Heading, Image, SectionIntro, Stack, Text} from '@primer/react-brand'

interface PricingGroupedProps {
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotEnterpriseSignupPath: string
  copilotContactSalesPath: string
}

interface Feature {
  title: string
  individual: boolean
  business: boolean
  enterprise: boolean
}

interface FeatureGroup {
  title: string
  features: Feature[]
}

// Set type as array of Feature
const chatFeatures: Feature[] = [
  {
    title: 'Unlimited messages, interactions, and history',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Context-aware coding support and explanations',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Debugging and security remediation assistance',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Repository-based semantic search',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'Access your knowledge base',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'EnterprisePull request DIFF analysis',
    individual: false,
    business: false,
    enterprise: true,
  },
  {
    title: 'Bing integration (beta)',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const codeCompletionFeatures: Feature[] = [
  {
    title: 'Code suggestions as you type',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Comments to code',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Fine-tuned models (coming soon)',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const smartActionsFeatures: Feature[] = [
  {
    title: 'Inline chat and prompt suggestions',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Slash commands and context variables',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Pull request description and summarization',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const devEnvFeatures: Feature[] = [
  {
    title: 'IDE, CLI and GitHub Mobile',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Chat support for general coding questions',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const managementPoliciesFeatures: Feature[] = [
  {
    title: 'IP indemnity',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'Public code filter',
    individual: true,
    business: true,
    enterprise: true,
  },
  {
    title: 'User/license management and SSO',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Data excluded from training by default',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Content exclusion',
    individual: false,
    business: true,
    enterprise: true,
  },
  {
    title: 'Requires GitHub Enterprise Cloud',
    individual: false,
    business: false,
    enterprise: true,
  },
]

const allFeatures: FeatureGroup[] = [
  {
    title: 'Chat',
    features: chatFeatures,
  },
  {
    title: 'Code completion',
    features: codeCompletionFeatures,
  },
  {
    title: 'Smart actions',
    features: smartActionsFeatures,
  },
  {
    title: 'Dev environments',
    features: devEnvFeatures,
  },
  {
    title: 'Management and policies',
    features: managementPoliciesFeatures,
  },
]

export function PricingGrouped({
  copilotSignupPath,
  copilotForBusinessSignupPath,
  copilotEnterpriseSignupPath,
  copilotContactSalesPath,
}: PricingGroupedProps) {
  return (
    <div>
      <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
        <Grid.Column span={12}>
          <SectionIntro fullWidth className="lp-SectionIntro">
            <SectionIntro.Label size="large" className="lp-Label--section">
              Pricing
            </SectionIntro.Label>
            <SectionIntro.Heading size="1" weight="bold">
              Take flight with GitHub Copilot.
            </SectionIntro.Heading>
            <SectionIntro.Description className="lp-SectionIntro-description--wide">
              Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and focus
              focus on doing what matters most: building great software.
            </SectionIntro.Description>
          </SectionIntro>
        </Grid.Column>

        <Grid.Column span={12}>
          <Grid className="lp-Pricing">
            <Grid.Column span={12} className="lp-Pricing-item lp-Pricing-item-narrow has-BlurredBg has-GradientBorder">
              <Stack direction={{narrow: 'vertical', regular: 'horizontal'}} gap={32} padding="none">
                {/* Copilot Individual */}
                <Stack padding="none" gap={24} style={{flex: '1'}}>
                  <Box>
                    <Text size="100" weight="semibold" className="lp-Label--pricing">
                      &nbsp;
                    </Text>
                    <Box marginBlockStart={8} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Individual
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For individual developers, freelancers, students, and educators that want to code faster and
                      happier.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                    <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                      $
                    </Text>
                    <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                      10
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                      per month / $100 USD per year
                    </Text>
                  </Stack>

                  <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                    <Button as="a" href={copilotSignupPath} block variant="primary">
                      Start a free trial
                    </Button>
                  </Stack>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-individual" />
                  <label htmlFor="features-individual" className="position-relative lp-Pricing-features-label">
                    What&apos; included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['individual'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['individual'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Business */}
                <Stack padding="none" gap={24} style={{flex: '1'}}>
                  <Box>
                    <Text size="100" weight="semibold" className="lp-Label--pricing">
                      Most popular
                    </Text>
                    <Box marginBlockStart={8} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Business
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For organizations ready to improve engineering velocity, code quality, and developer experience.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                    <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                      $
                    </Text>
                    <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                      19
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                      per user / month
                    </Text>
                  </Stack>

                  <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                    <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                      Buy now
                    </Button>
                  </Stack>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-business" />
                  <label htmlFor="features-business" className="position-relative lp-Pricing-features-label">
                    What&apos; included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['business'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['business'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>

                <hr className="lp-Pricing-divider lp-Pricing-divider--grouped" />

                {/* Copilot Enterprise */}
                <Stack padding="none" gap={24} style={{flex: '1'}}>
                  <Box>
                    <Text size="100" weight="semibold" className="lp-Label--pricing">
                      New
                    </Text>
                    <Box marginBlockStart={8} marginBlockEnd={16}>
                      <Heading as="h3" size="5">
                        Copilot Enterprise
                      </Heading>
                    </Box>
                    <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                      For companies looking to customize Copilot to their organization and infuse AI across the
                      developer workflow.
                    </Text>
                  </Box>

                  <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                    <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                      $
                    </Text>
                    <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                      39
                    </Text>
                    <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                      per user / month
                    </Text>
                  </Stack>

                  <Button as="a" href={copilotEnterpriseSignupPath} variant="primary">
                    Buy now
                  </Button>

                  <hr className="lp-Pricing-divider" />
                  <input type="checkbox" className="d-none lp-Pricing-features-toggle" id="features-enterprise" />
                  <label htmlFor="features-enterprise" className="position-relative lp-Pricing-features-label">
                    What&apos; included
                    <div className="lp-Pricing-features-icon position-absolute top-0 right-0 bottom-0 d-md-none">
                      <ChevronDownIcon />
                    </div>
                  </label>
                  {allFeatures.map((group: FeatureGroup, i) => (
                    <Box key={i} className="lp-Pricing-features-box">
                      <Text size="200" weight="normal" className="lp-Pricing-feature-group-title">
                        {group.title}
                      </Text>
                      <ul className="lp-Pricing-list">
                        {group.features.map((feature: Feature, j) => (
                          <li key={j} className={feature['enterprise'] ? 'lp-Pricing-list-check' : 'lp-Pricing-list-x'}>
                            {feature['enterprise'] ? <CheckIcon /> : <XIcon />} {feature['title']}
                          </li>
                        ))}
                      </ul>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid.Column>
          </Grid>
        </Grid.Column>

        <Grid.Column span={12} className="mt-8">
          <Grid className="lp-Pricing">
            <Grid.Column
              span={12}
              className="lp-Pricing-item has-BlurredBg has-GradientBorder"
              style={{gap: '16px', backgroundColor: 'rgba(22, 27, 34, 0.85)'}}
            >
              <Heading as="h4" size="5">
                Need human help?
              </Heading>

              <Stack
                direction={{narrow: 'vertical', regular: 'horizontal'}}
                gap={24}
                padding="none"
                className="lp-Pricing--multiColumn"
              >
                <Box style={{flex: '1'}} className="col-12 col-md-9">
                  <Box>
                    <Text as="p" size="200" weight="normal" variant="muted">
                      Let’s define how to propel your team into a new era.
                    </Text>
                  </Box>
                </Box>

                <Box className="col-12 col-md-3">
                  <Button as="a" href={copilotContactSalesPath} block variant="secondary">
                    Contact sales
                  </Button>
                </Box>
              </Stack>
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>

      {/* Spacer */}
      <Box paddingBlockStart={32} aria-hidden={true} />

      <div className="lp-Pricing-bgCover">
        <Image
          className="lp-Pricing-bgCoverImage"
          src="/images/modules/site/copilot/pricing-cover.svg"
          loading="lazy"
          alt=""
        />
      </div>
    </div>
  )
}

try{ PricingGrouped.displayName ||= 'PricingGrouped' } catch {}